import React from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from "@mui/material";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Generic Confirmation Dialog Component
 *
 * A reusable confirmation modal that can be used for any confirmation scenario.
 * Supports custom titles, messages, and button variants for different use cases.
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when user confirms the action
 * @param {string} title - Dialog title (e.g., "Start Session", "End Session")
 * @param {string|ReactNode} message - Dialog message content (can be JSX)
 * @param {string} confirmButtonText - Text for confirm button (default: "Confirm")
 * @param {string} cancelButtonText - Text for cancel button (default: "Cancel")
 * @param {string} confirmButtonVariant - MUI button variant/color (default: "primary")
 * @param {boolean} showWarningIcon - Show warning icon in dialog (default: true)
 */
const ConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmButtonText = "Confirm",
    cancelButtonText = "Cancel",
    confirmButtonVariant = "primary",
    showWarningIcon = true,
}) => {

    const handleConfirm = () => {
        onConfirm();
    };

    const handleClose = () => {
        onClose();
    };

    // Determine button color based on variant
    const getButtonColor = () => {
        if (confirmButtonVariant === "danger") {
            return "error";
        }
        return confirmButtonVariant;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth className="confirmationDialog">
            <DialogContent>
                {/* Title with optional warning icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {showWarningIcon && (
                        <WarningAmberIcon
                            sx={{
                                color: confirmButtonVariant === "danger" ? "#d32f2f" : "#ff9800",
                                fontSize: 28
                            }}
                        />
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>

                {/* Message content - can be string or JSX */}
                <Box sx={{ color: '#555' }}>
                    {typeof message === 'string' ? (
                        <Typography>{message}</Typography>
                    ) : (
                        message
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {/* Cancel button */}
                <Button
                    onClick={handleClose}
                    sx={{
                        color: "#757575",
                        fontWeight: 500,
                        textTransform: "uppercase",
                    }}
                >
                    {cancelButtonText}
                </Button>

                {/* Confirm button */}
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    color={getButtonColor()}
                    sx={{
                        textTransform: "uppercase",
                        px: 3,
                        fontWeight: 500,
                    }}
                >
                    {confirmButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmationDialog;
